<template>
  <nav
    id="sidebar"
    v-if="$route.path !== '/login'"
    @mouseenter="expanded = true"
    @mouseleave="expanded = pinned || false"
    class="ui left vertical sidebar inverted labeled menu thin visible"
    :class="[{ expanded: expanded, contracted: !expanded, pinned: pinned }, colorClass]"
  >
    <div class="item logo" v-if="product || logo" @click="$router.push({ name: 'home' })">
      <img class="item nav logo" :src="logo" :alt="product" v-if="logo" />
      {{ product }}
    </div>

    <!-- Applications -->
    <router-link class="item capitalize-fl" :to="{ name: 'applications' }" :active-class="'active'">
      <i class="icon cube"></i>
      {{ $tc('APPLICATION', 2) }}
    </router-link>

    <!-- Flows -->
    <router-link class="item capitalize-fl" :to="{ name: 'flows' }" :active-class="'active'">
      <i class="shuffle icon"></i>
      {{ $tc('FLOW', 2) }}
    </router-link>

    <!-- User -->
    <router-link class="item capitalize-fl" :to="{ name: 'users' }" :active-class="'active'">
      <i class="icon user"></i>
      {{ $tc('USER', 2) }}
    </router-link>

    <!-- Groups -->
    <router-link class="item capitalize-fl" :to="{ name: 'groups' }" :active-class="'active'">
      <i class="icon group"></i>
      {{ $tc('GROUP', 2) }}
    </router-link>

    <!-- Tables -->
    <router-link class="item capitalize-fl" :to="{ name: 'tables' }" :active-class="'active'">
      <i class="icon table"></i>
      {{ $tc('TABLE', 2) }}
    </router-link>

    <!-- Services -->
    <div
      v-if="serviceTypes.length > 0"
      class="item capitalize-fl"
      :to="{ name: 'services' }"
      :active-class="'active'"
    >
      {{ $tc('SERVICE', 2) }}
      <div class="menu">
        <router-link
          v-for="serviceType in serviceTypes"
          class="item capitalize-fl"
          :to="{ name: 'services', params: { serviceType: serviceType.name } }"
          :active="'active'"
          :key="serviceType.name"
        >
          <i class="icon" :class="serviceType.icon"></i>
          {{ $tc(serviceType.name + '_SERVICE', 2) }}
        </router-link>
      </div>
    </div>

    <!-- Manatees -->
    <div
      class="item capitalize-fl"
      v-if="
        $can('access', 'manatee_settings') ||
          (manatees > 0 && $can('access', 'estero')) ||
          $can('access', 'flow_restrictions')
      "
    >
      {{ $tc('MANATEE', 2) }}
      <div class="menu">
        <!-- Settings -->
        <router-link
          class="item capitalize-fl"
          :to="{ name: 'settings' }"
          :active-class="'active'"
          v-if="$can('access', 'manatee_settings')"
        >
          {{ $tc('SETTING', 2) }}
          <i class="icon sliders horizontal"></i>
        </router-link>

        <!-- Restrictions -->
        <router-link
          class="item capitalize-fl"
          :to="{ name: 'restrictions' }"
          :active-class="'active'"
          v-if="$can('access', 'flow_restrictions')"
        >
          {{ $tc('RESTRICTION', 2) }}
          <i class="icon key horizontal"></i>
        </router-link>

        <!-- Conductor/Estero -->
        <router-link
          class="item capitalize-fl"
          :to="{ name: 'estero' }"
          :active-class="'active'"
          v-if="manatees > 0 && $can('access', 'estero')"
        >
          <div class="ui small label" :class="[colorClass]">{{ friendlyManateeCount }}</div>
          {{ $t('ESTERO') }}
        </router-link>

        <!-- Alerting --->
        <router-link
          class="item capitalize-fl"
          :to="{ name: 'alerts' }"
          :active-class="'active'"
          v-if="$can('access', 'estero')"
        >
          <div class="ui red small label" :class="[colorClass]" v-if="activeAlertCount > 0">
            {{ activeAlertCount }}
          </div>
          {{ $tc('ALERT', 2) }}
          <i class="icon bell" v-if="activeAlertCount == 0"></i>
        </router-link>
      </div>
    </div>

    <!-- Basic setup -->
    <div class="item capitalize-fl">
      {{ $tc('BASIC_SETUP', 2) }}
      <div class="menu">
        <router-link class="item capitalize-fl" :to="{ name: 'subjects' }" :active-class="'active'">
          {{ $tc('SUBJECT', 2) }}
          <i class="icon book"></i>
        </router-link>
        <router-link
          class="item capitalize-fl"
          :to="{ name: 'manufacturers' }"
          :active-class="'active'"
        >
          {{ $tc('MANUFACTURER', 2) }}
          <i class="icon factory"></i>
        </router-link>
        <router-link
          class="item capitalize-fl"
          :to="{ name: 'i18n' }"
          :active-class="'active'"
          v-if="superUser"
        >
          {{ $tc('LOCALIZATION', 2) }}
          <i class="globe icon" />
        </router-link>
        <router-link class="item capitalize-fl" :to="{ name: 'hubs' }" :active-class="'active'">
          {{ $tc('HUB', 2) }}
          <i class="icon store"></i>
        </router-link>
      </div>
    </div>

    <div class="bottom menu">
      <transition
        enter-active-class="animate__animated animate__fadeIn"
        leave-active-class="animate__animated animate__fadeOut"
      >
        <div
          class="ui dropdown upward item"
          v-show="allTasks.length > 0"
          @click="showJobs = !showJobs"
          :class="{ 'visible active': showJobs && allTasks.length > 0 }"
        >
          <span class="tiny">{{ $t('JOBS') | capitalize }}</span>
          <i class="ui icon active loading circle notch" v-if="pendingTasks.length > 0" />
          <div
            class="ui inverted transitioning menu"
            :style="{ display: showJobs && allTasks.length > 0 ? 'block !important' : 'none' }"
            :class="[{ visible: showJobs && allTasks.length > 0 }, colorClass]"
          >
            <div
              class="item"
              v-for="(task, idx) in allTasks"
              :key="idx"
              style="color: rgba(255, 255, 255, 0.5) !important;"
            >
              {{ task.description | capitalize }}
              <span
                class="ui right floated"
                v-if="task.complete"
                style="color: rgba(255, 255, 255, 0.2) !important;"
                >{{ task.ms }}ms</span
              >
            </div>
          </div>
        </div>
      </transition>

      <a href="#" class="item capitalize-fl" @click.prevent="pinned = !pinned">
        <i class="icon toggle" :class="{ on: pinned, off: !pinned }"></i>
        {{ $tc('PINMENU', 2) }}
      </a>
      <router-link
        :to="{ name: 'preferences' }"
        class="item username"
        :title="currentUser"
        :active-class="'active'"
      >
        <i class="user cog icon"></i>
        <span class="tiny">{{ currentUser }}</span>
      </router-link>
      <router-link
        class="item capitalize-fl"
        :to="{ name: 'logout' }"
        :active-class="'active'"
        v-if="allowLogout"
      >
        <i class="icon lock"></i>
        {{ $tc('LOGOUT', 2) }}
      </router-link>
      <div class="item" title="Version for Cuesta / Kwanza">
        <i class="code branch icon"></i>
        <span class="tiny">{{ version.cuesta }} / {{ version.kwanza }}</span>
      </div>
      <div
        class="item"
        :class="[env.color ? 'active ' + env.color : '']"
        :title="$t('ENVIRONMENT')"
        v-if="env && env.name"
      >
        <i class="icon" :class="[env.icon]" v-if="env.icon" />
        <span class="tiny">{{ env.name }}</span>
      </div>
      <a
        :href="stellerUrlForBrowser"
        target="_blank"
        rel="noreferrer"
        class="active red item capitalize-fl"
        v-if="stellerMissing"
      >
        <i class="plug icon" />
        {{ $tc('MENU_SIRENIA_EXTENSION') }}</a
      >
    </div>
  </nav>
</template>

<style lang="scss">
@charset "utf-8";
@import '../../../scss/variables';

nav#sidebar {
  transition: all 0.5s, transform 0.5s;
  transition-delay: 100ms;
  // NOTE: this was the best the internet had to offer for having dropdowns in a sidebar menu
  overflow: visible !important;

  + main.pusher {
    transition: all 0.5s, transform 0.5s;
    transition-delay: 100ms;
    margin-right: 0;
  }

  &.contracted {
    transform: translate3d(calc(-100% + 10px), 0, 0);
    + main.pusher {
      transform: translate3d(10px, 0, 0);
    }
  }

  &.expanded {
    transform: translate3d(0, 0, 0);
  }

  &.pinned {
    + main.pusher {
      margin-right: 140px;
    }
  }

  .ui.dropdown.upward .ui.menu {
    left: 101%;
    border-top-left-radius: 4px;
  }
}

.ui.menu.vertical .bottom.menu {
  position: fixed;
  left: 0px;
  right: 0px;
  bottom: 0px;
}

.item.username {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.item.logo {
  font-weight: bold !important;
  text-align: center;
  cursor: pointer;
  img {
    width: 80%;
    margin: auto !important;
    display: block;
  }
}
</style>

<script>
// @flow
import { Service, Auth, Estero, EsteroAlertLog } from 'services'
import { SET_MAIN_MENU_PINNED } from '../../../store/mutation-types'

export default {
  name: 'app-menu',

  data() {
    return {
      expanded: true,
      manatees: 0,
      showJobs: false
    }
  },

  computed: {
    product() {
      const n = this.$i18n.t('PRODUCT_NAME')
      // if empty or default value
      return !n || n === 'PRODUCT_NAME' ? '' : n
    },
    logo() {
      const l = this.$i18n.t('PRODUCT_LOGO')
      // if empty or default value
      if (!l || l === 'PRODUCT_LOGO') return ''
      // append data:image... if it isnt alread there
      return l.indexOf('data:') === 0 ? l : 'data:image/png;base64,' + l
    },
    pinned: {
      get() {
        return this.$store.state.mainMenuPinned
      },
      set(p) {
        this.$store.commit(SET_MAIN_MENU_PINNED, p)
      }
    },
    stellerMissing() {
      return this.stellerDetected != null && !this.stellerDetected
    },
    stellerDetected() {
      return this.$store.state.debugger.stellerDetected
    },
    currentUser() {
      return this.$store.state.currentUser || Auth.getLoggedInUser()
    },
    version() {
      return this.$store.state.config.version
    },
    env() {
      return this.$store.state.config.env
    },
    colorClass() {
      return this.$store.state.config.color
    },
    serviceTypes() {
      return Service.serviceTypes()
    },
    friendlyManateeCount() {
      var c = this.manatees
      // We round to nearest K if a large number
      if (c > 999) {
        c = `${Math.floor(c / 1000)}K`
      }
      return c
    },
    activeAlertCount() {
      return _.filter(this.$store.state.estero_alert_log.items, l =>
        l.log ? l.log[l.log.length - 1].active : false
      ).length
    },
    allowLogout() {
      return !Auth.isUserPersistent()
    },
    superUser() {
      return Auth.isUserSuper()
    },
    stellerUrlForBrowser() {
      const chromeStoreUrl =
        'https://chrome.google.com/webstore/detail/sirenia-web-automation-dr/lbokehfhhclmidfioacglfeigplbccdj'
      const firefoxUrl = 'https://firefox.sirenia.io'

      if (typeof chrome !== 'undefined') {
        // The chromium based edge browser has 'Edg' as the browser specific token
        if (navigator.userAgent.match(/ Edg\//)) return chromeStoreUrl // For now, just point to chrome store
        return chromeStoreUrl
      } else if (navigator.userAgent.match(/ Firefox\//)) {
        return firefoxUrl
      }
      console.warn('Unrecognized browser: ', navigator.userAgent)
      return chromeStoreUrl
    },
    allTasks() {
      return this.$store.state.pendingTasks
    },
    pendingTasks() {
      return this.allTasks.filter(t => !t.complete)
    }
  },

  methods: {
    countManatees() {
      if (Auth.isUserLoggedIn())
        Estero.count()
          .then(response => (this.manatees = response.data))
          .catch(() => (this.manatees = -1))
      setTimeout(this.countManatees, 10000)
    }
  },

  mounted() {
    this.countManatees()
    EsteroAlertLog.readAll()
  }
}
</script>
